import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { createContext, lazy, Suspense, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

const SwipeToast = lazy(() => import('./SwipeToast/SwipeToast'))

type Aviso = { id: number; tipo: 'ok' | 'error'; titulo: string; texto?: string }

type Avisar = (aviso: Omit<Aviso, 'id'>) => void

const Contexto = createContext<Avisar | null>(null)

/**
 * Avisos breves de éxito o error (SwipeToast): uno a la vez, se pueden deslizar para descartar, se pausan
 * al pasar el puntero o enfocar y se anuncian con aria-live. Nada crítico depende de ellos: los errores de
 * formulario siguen junto a su campo.
 */
export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [aviso, setAviso] = useState<Aviso | null>(null)
  const avisar = useCallback<Avisar>((a) => setAviso({ ...a, id: Date.now() }), [])
  const valor = useMemo(() => avisar, [avisar])

  return (
    <Contexto.Provider value={valor}>
      {children}
      {aviso && (
        <Suspense fallback={null}>
        <SwipeToast
          key={aviso.id}
          title={aviso.titulo}
          description={aviso.texto}
          icon={aviso.tipo === 'ok' ? <CheckCircle2 size={20} strokeWidth={1.75} /> : <AlertTriangle size={20} strokeWidth={1.75} />}
          background="var(--marino)"
          color="var(--marino-ink)"
          fuseColor={aviso.tipo === 'ok' ? '#7fd1a4' : '#ffb4a8'}
          radius={8}
          duration={6000}
          closeButton
          onClose={() => setAviso((actual) => (actual?.id === aviso.id ? null : actual))}
        />
        </Suspense>
      )}
    </Contexto.Provider>
  )
}

export function useAvisos(): Avisar {
  const c = useContext(Contexto)
  if (!c) throw new Error('useAvisos debe usarse dentro de ProveedorAvisos')
  return c
}
